/**
 * AxLabs Experiments - Enzyme Kinetics (Michaelis-Menten)
 *
 * Demonstrates enzyme-catalyzed reactions and their kinetic parameters.
 * Uses the Michaelis-Menten model with optional inhibition.
 *
 * Governing equation: v = Vmax[S] / (Km + [S])
 */
import { BaseSimulationEngine, DIMENSIONS, rk4StepNumeric, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'initialSubstrate',
        name: 'Initial Substrate Concentration',
        symbol: '[S]_0',
        unit: 'mM',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0.01,
        max: 100,
        default: 10,
        step: 0.5,
        description: 'Starting concentration of substrate',
    },
    {
        id: 'vmax',
        name: 'Maximum Reaction Rate',
        symbol: 'V_{max}',
        unit: 'mM/s',
        dimension: { L: -3, M: 0, T: -1, I: 0, Θ: 0, N: 1, J: 0 },
        min: 0.001,
        max: 10,
        default: 1,
        step: 0.1,
        description: 'Maximum reaction rate when enzyme is saturated',
    },
    {
        id: 'km',
        name: 'Michaelis Constant',
        symbol: 'K_m',
        unit: 'mM',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0.01,
        max: 50,
        default: 2,
        step: 0.1,
        description: 'Substrate concentration at half-maximal velocity',
    },
    {
        id: 'inhibitorConc',
        name: 'Inhibitor Concentration',
        symbol: '[I]',
        unit: 'mM',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0,
        max: 20,
        default: 0,
        step: 0.5,
        description: 'Concentration of competitive inhibitor (0 = no inhibition)',
    },
    {
        id: 'ki',
        name: 'Inhibitor Constant',
        symbol: 'K_i',
        unit: 'mM',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0.01,
        max: 50,
        default: 5,
        step: 0.1,
        description: 'Dissociation constant for enzyme-inhibitor complex',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'michaelis_menten',
        name: 'Michaelis-Menten Equation',
        latex: 'v = \\frac{V_{max}[S]}{K_m + [S]}',
        variables: ['reactionRate', 'vmax', 'substrateConc', 'km'],
        description: 'Fundamental equation relating reaction rate to substrate concentration',
        condition: (params) => (params['inhibitorConc'] ?? 0) === 0,
    },
    {
        id: 'competitive_inhibition',
        name: 'Competitive Inhibition',
        latex: 'v = \\frac{V_{max}[S]}{K_m\\left(1 + \\frac{[I]}{K_i}\\right) + [S]}',
        variables: ['reactionRate', 'vmax', 'substrateConc', 'km', 'inhibitorConc', 'ki'],
        description: 'Michaelis-Menten with competitive inhibitor',
        condition: (params) => (params['inhibitorConc'] ?? 0) > 0,
    },
    {
        id: 'half_vmax',
        name: 'At Km',
        latex: 'v = \\frac{V_{max}}{2} \\text{ when } [S] = K_m',
        variables: ['reactionRate', 'vmax', 'km'],
        description: 'Reaction rate is half of Vmax when [S] equals Km',
    },
    {
        id: 'lineweaver_burk',
        name: 'Lineweaver-Burk',
        latex: '\\frac{1}{v} = \\frac{K_m}{V_{max}}\\cdot\\frac{1}{[S]} + \\frac{1}{V_{max}}',
        variables: ['reactionRate', 'km', 'vmax', 'substrateConc'],
        description: 'Double reciprocal plot (1/v vs 1/[S]) gives a straight line',
    },
    {
        id: 'substrate_depletion',
        name: 'Substrate Depletion',
        latex: '\\frac{d[S]}{dt} = -v',
        variables: ['substrateConc', 'reactionRate'],
        description: 'Substrate is consumed at the reaction rate',
    },
    {
        id: 'product_formation',
        name: 'Product Formation',
        latex: '\\frac{d[P]}{dt} = v',
        variables: ['productConc', 'reactionRate'],
        description: 'Product is formed at the reaction rate',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'substrate_depleted',
        name: 'Substrate Depleted',
        description: 'All substrate has been converted to product',
        check: (state) => (state.variables['substrateConc'] ?? 1) < 0.001,
        severity: 'warning',
        educationalMessage: 'The substrate has been completely consumed. Add more substrate to continue the reaction.',
    },
    {
        id: 'inhibition_too_strong',
        name: 'Strong Inhibition',
        description: 'Inhibitor concentration severely reduces reaction rate',
        check: (state) => (state.variables['fractionVmax'] ?? 1) < 0.1,
        severity: 'warning',
        educationalMessage: 'The reaction is more than 90% inhibited. The inhibitor concentration is very high relative to substrate.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const ENZYME_KINETICS_CONFIG = {
    id: 'enzyme-kinetics',
    slug: 'enzyme-kinetics',
    name: 'Enzyme Kinetics (Michaelis-Menten)',
    domain: 'biology',
    description: 'Explore enzyme-catalyzed reactions and inhibition',
    longDescription: `
Enzyme kinetics describes how enzymes catalyze biochemical reactions.
The Michaelis-Menten model is fundamental to understanding enzyme behavior.

Key concepts:
- Vmax: Maximum reaction rate when enzyme is saturated
- Km: Substrate concentration at half-maximal velocity (indicates enzyme-substrate affinity)
- Saturation: At high [S], rate approaches Vmax asymptotically
- Competitive inhibition: Inhibitor competes with substrate for active site
- Lineweaver-Burk plot: Double reciprocal plot for linear analysis

Enzyme efficiency:
- Low Km = high affinity (enzyme binds substrate tightly)
- High kcat/Km = catalytic efficiency

This simulation demonstrates:
- Hyperbolic kinetics (v vs [S])
- Effect of Km and Vmax on reaction rate
- How competitive inhibitors affect kinetics
- Time course of substrate consumption
  `.trim(),
    version: 1,
    conceptTags: ['enzyme', 'kinetics', 'michaelis-menten', 'inhibition', 'biochemistry'],
    difficulty: 4,
    prerequisites: ['chemical-kinetics', 'proteins'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'substrateConc', type: 'parameter', parameterId: 'initialSubstrate' },
        { variableId: 'productConc', type: 'fixed', value: 0 },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 0.01,
    maxDt: 0.1,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class EnzymeKineticsSimulation extends BaseSimulationEngine {
    config = ENZYME_KINETICS_CONFIG;
    constructor() {
        super({ substrateConc: 10, productConc: 0, reactionRate: 0, fractionVmax: 0 }, { type: 'rk4', dt: 0.01, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const S0 = params['initialSubstrate'] ?? 10;
        const v = this.calculateRate(S0, params);
        const Vmax = params['vmax'] ?? 1;
        return {
            substrateConc: S0,
            productConc: 0,
            reactionRate: v,
            fractionVmax: v / Vmax,
        };
    }
    /**
     * Calculate reaction rate using Michaelis-Menten equation
     */
    calculateRate(S, params) {
        const Vmax = params['vmax'] ?? 1;
        const Km = params['km'] ?? 2;
        const I = params['inhibitorConc'] ?? 0;
        const Ki = params['ki'] ?? 5;
        // Apparent Km with competitive inhibition
        const KmApp = Km * (1 + I / Ki);
        // Michaelis-Menten
        return (Vmax * S) / (KmApp + S);
    }
    computeDerivatives(state, params) {
        const v = this.calculateRate(state.substrateConc, params);
        return {
            substrateConc: -v,
            productConc: v,
        };
    }
    integrate(dt) {
        // Use RK4 for accuracy
        const derivative = (s, _t) => {
            const v = this.calculateRate(s.substrateConc, this.parameters);
            return {
                substrateConc: -v,
                productConc: v,
                reactionRate: 0,
                fractionVmax: 0,
            };
        };
        const newState = rk4StepNumeric(this.state, derivative, this.time, dt);
        // Ensure non-negative concentrations
        this.state.substrateConc = Math.max(0, newState.substrateConc ?? 0);
        this.state.productConc = Math.max(0, newState.productConc ?? 0);
        // Update derived values
        const Vmax = this.parameters['vmax'] ?? 1;
        this.state.reactionRate = this.calculateRate(this.state.substrateConc, this.parameters);
        this.state.fractionVmax = this.state.reactionRate / Vmax;
    }
    getPositionForStabilityCheck() {
        return this.state.substrateConc;
    }
    /**
     * Generate v vs [S] curve data (for Michaelis-Menten plot)
     */
    generateVvsS(substrates) {
        return substrates.map(S => ({
            substrate: S,
            rate: this.calculateRate(S, this.parameters),
        }));
    }
    /**
     * Generate Lineweaver-Burk plot data
     */
    generateLineweaverBurk(substrates) {
        return substrates
            .filter(S => S > 0)
            .map(S => {
            const v = this.calculateRate(S, this.parameters);
            return {
                invSubstrate: 1 / S,
                invRate: v > 0 ? 1 / v : Infinity,
            };
        })
            .filter(point => Number.isFinite(point.invRate));
    }
    /**
     * Calculate apparent Km (with inhibition)
     */
    getApparentKm() {
        const Km = this.parameters['km'] ?? 2;
        const I = this.parameters['inhibitorConc'] ?? 0;
        const Ki = this.parameters['ki'] ?? 5;
        return Km * (1 + I / Ki);
    }
}
/**
 * Factory function to create a new enzyme kinetics simulation
 */
export function createEnzymeKinetics() {
    return new EnzymeKineticsSimulation();
}
//# sourceMappingURL=enzyme-kinetics.js.map